/**
 * Custom Video with Icons
 *
 * - Icon grid layout (CSS variables)
 * - Video fallback: poster when video fails to load or autoplay is blocked
 * - Data Saver detection (navigator.connection.saveData)
 * - Error/suspend/stalled/abort handling
 *
 * Runs before theme.js (section script loads first) for saveData handling.
 */

(function() {
  'use strict';

  const hasSaveData = () => {
    try {
      return navigator.connection?.saveData === true;
    } catch {
      return false;
    }
  };

  const supportsVideo = () => {
    const v = document.createElement('video');
    return !!v.canPlayType;
  };

  /* ------ Icon Grid ------ */
  document.querySelectorAll('.custom-video-with-icons').forEach(function(section) {
    const grid = section.querySelector('.custom-video-with-icons__grid');
    if (grid) {
      const iconCount = grid.children.length;
      grid.style.setProperty('--icon-count', Math.min(iconCount, 4));
      if (iconCount === 1) grid.classList.add('custom-video-with-icons__grid--single');
      else if (iconCount === 2) grid.classList.add('custom-video-with-icons__grid--double');
      else if (iconCount === 3) grid.classList.add('custom-video-with-icons__grid--triple');
      else if (iconCount >= 4) grid.classList.add('custom-video-with-icons__grid--quad');
    }
  });

  /* ------ Video Fallback ------ */
  if (!supportsVideo()) return;

  const fallbackWrappers = document.querySelectorAll('[data-video-fallback]');
  fallbackWrappers.forEach(function(wrapper) {
    const videoMediaEls = wrapper.querySelectorAll('video-media');
    if (!videoMediaEls.length) return;

    const enterFallback = (videoMedia) => {
      if (videoMedia.hasAttribute('data-fallback-active')) return;
      videoMedia.setAttribute('data-fallback-active', '');
      wrapper.classList.add('has-video-fallback');
      const playBtn = wrapper.querySelector('[data-video-fallback-play]');
      if (playBtn) {
        playBtn.classList.remove('video-play-button--fallback-only');
        playBtn.hidden = false;
      }
    };

    const setupVideoFallback = (videoMedia) => {
      const video = videoMedia.querySelector('video');
      if (!video) return;

      const onError = () => enterFallback(videoMedia);
      video.addEventListener('error', onError);
      video.addEventListener('suspend', onError);
      video.addEventListener('stalled', onError);
      video.addEventListener('abort', onError);

      /* Play rejection (autoplay blocked) */
      const origPlay = video.play.bind(video);
      video.play = function() {
        return origPlay().catch((err) => {
          if (err?.name === 'NotAllowedError') enterFallback(videoMedia);
          throw err;
        });
      };
    };

    const setupDataSaverMode = (videoMedia) => {
      if (!videoMedia.hasAttribute('data-saver-mode') && !hasSaveData()) return false;
      const video = videoMedia.querySelector('video');
      if (!video) return false;

      if (!videoMedia.hasAttribute('data-saver-mode')) {
        videoMedia.removeAttribute('autoplay');
        video.setAttribute('preload', 'none');
        videoMedia.setAttribute('data-saver-mode', '');
        wrapper.classList.add('has-video-fallback', 'has-data-saver');
      }

      const playBtn = wrapper.querySelector('[data-video-fallback-play]');
      if (playBtn) {
        playBtn.classList.remove('video-play-button--fallback-only');
        playBtn.hidden = false;
      }

      const onPlayClick = () => {
        video.setAttribute('preload', 'auto');
        video.load();
        videoMedia.setAttribute('autoplay', '');
        videoMedia.play?.();
      };

      playBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        onPlayClick();
      });

      videoMedia.addEventListener('click', onPlayClick, { once: true });
      return true;
    };

    videoMediaEls.forEach(function(videoMedia) {
      if (videoMedia.hasAttribute('host')) return;

      if (videoMedia.hasAttribute('data-saver-mode') || hasSaveData()) {
        setupDataSaverMode(videoMedia);
      } else {
        setupVideoFallback(videoMedia);
      }
    });

    /* Fallback play button click */
    wrapper.querySelectorAll('[data-video-fallback-play]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        if (!wrapper.classList.contains('has-video-fallback')) return;
        e.stopPropagation();
        const vm = wrapper.querySelector('video-media');
        if (vm) vm.play?.();
      });
    });
  });
})();
